import { useAtom, useAtomValue } from 'jotai';
import type { GeoJSONSource, MapTouchEvent } from 'maplibre-gl';
import { useEffect, useRef, type RefObject } from 'react';
import { useMap, useMapEvent } from '@/features/map';
import { cellAt, cellLngLat, pathCells } from '../model/result';
import { inspectionAtom, resultAtom } from '../state/atoms';
import { EMPTY_FEATURES, PATH_SOURCE } from './glideStyle';

/**
 * Inspects the result cell under the pointer and draws the flight path of the inspected cell.
 * A mouse inspects on hover. A finger, which cannot hover, inspects from the moment it touches the
 * result and while it drags (the map does not pan then); the inspection stays after it lifts.
 */
export function HoverPath({ pointer }: { pointer: RefObject<string> }) {
  const map = useMap();
  const result = useAtomValue(resultAtom);
  const [inspection, setInspection] = useAtom(inspectionAtom);
  /** A one-finger touch that started inside the result is scrubbing through it. */
  const scrubbing = useRef(false);

  useMapEvent('mousemove', (e) => {
    // Touch screens emulate a mousemove on tap; that must not replace a pinned inspection.
    if (pointer.current !== 'mouse') return;
    const k = cellAt(result, e.lngLat);
    e.target.getCanvas().style.cursor = k < 0 ? '' : 'crosshair';
    setInspection(k < 0 ? null : { cell: k, at: e.lngLat, pinned: false });
  });

  const inspectTouch = (e: MapTouchEvent) => {
    const k = cellAt(result, e.lngLat);
    // Dragging out of the result keeps the last cell rather than losing it.
    if (k >= 0) setInspection({ cell: k, at: e.lngLat, pinned: true });
  };

  useMapEvent('touchstart', (e) => {
    // Only touches on the map itself: not a second finger (pinch zoom) or a label's button.
    scrubbing.current =
      e.points.length === 1 &&
      e.originalEvent.target === e.target.getCanvas() &&
      cellAt(result, e.lngLat) >= 0;
    if (!scrubbing.current) return;
    e.preventDefault(); // no panning while scrubbing
    inspectTouch(e);
  });

  useMapEvent('touchmove', (e) => {
    if (scrubbing.current && e.points.length === 1) inspectTouch(e);
  });

  useEffect(() => {
    let data = EMPTY_FEATURES;
    if (result && inspection) {
      // From the exact point (not its cell's centre) along the cell's path.
      const { at, cell } = inspection;
      const coords = [
        [at.lng, at.lat],
        ...pathCells(result, cell)
          .slice(1)
          .map((c) => cellLngLat(result, c)),
      ];
      data = {
        type: 'FeatureCollection',
        features: [
          { type: 'Feature', properties: {}, geometry: { type: 'LineString', coordinates: coords } },
          { type: 'Feature', properties: {}, geometry: { type: 'Point', coordinates: coords[0] } },
        ],
      };
    }
    map?.getSource<GeoJSONSource>(PATH_SOURCE)?.setData(data);
  }, [map, result, inspection]);

  return null;
}
